"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { BookOpen, Eye, GitBranch, Layers, Users, Zap } from "lucide-react";
import { useIntlayer } from "next-intlayer";
import { useTheme } from "next-themes";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

const FEATURES_META: { number: string; icon: LucideIcon }[] = [
  { number: "01", icon: Eye },
  { number: "02", icon: Layers },
  { number: "03", icon: GitBranch },
  { number: "04", icon: Zap },
  { number: "05", icon: BookOpen },
  { number: "06", icon: Users },
];

function FeatureItem({
  feature,
}: {
  feature: { number: string; icon: LucideIcon; title: ReactNode; description: ReactNode };
}) {
  const ref = useRef<HTMLDivElement>(null);
  const Icon = feature.icon;
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 0.3, 0.5, 0.7, 1], [0.88, 1.0, 1.05, 1.0, 0.88]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.5, 0.7, 1], [0.3, 0.85, 1, 0.85, 0.3]);
  const blurPx = useTransform(scrollYProgress, [0, 0.3, 0.5, 0.7, 1], [4, 0, 0, 0, 4]);
  const filter = useTransform(blurPx, (b) => `blur(${b}px)`);

  const numColorLight = useTransform(
    scrollYProgress,
    [0, 0.3, 0.5, 0.7, 1],
    ["#f0f9ff", "#cceeff", "#0193cd", "#cceeff", "#f0f9ff"],
  );
  const numColorDark = useTransform(
    scrollYProgress,
    [0, 0.3, 0.5, 0.7, 1],
    ["#0d1820", "#0a3050", "#0193cd", "#0a3050", "#0d1820"],
  );
  const numColor = mounted && resolvedTheme === "dark" ? numColorDark : numColorLight;

  return (
    <motion.div
      ref={ref}
      style={{ scale, opacity, filter }}
      className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 md:gap-16 py-12 border-b border-border origin-center"
    >
      <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-3">
        <motion.span
          className="text-7xl md:text-8xl font-bold leading-none select-none tabular-nums"
          style={{ color: numColor }}
        >
          {feature.number}
        </motion.span>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary/10">
          <Icon size={18} className="text-[#0193cd]" />
        </div>
      </div>

      <div className="flex flex-col justify-center">
        <h3 className="text-2xl md:text-3xl font-semibold mb-3 text-foreground">{feature.title}</h3>
        <p className="text-base leading-relaxed max-w-lg text-muted-foreground">
          {feature.description}
        </p>
      </div>
    </motion.div>
  );
}

export default function WhySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const { sectionLabel, sectionTitle, features } = useIntlayer("why-section");

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start center", "end center"],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const featuresData = FEATURES_META.map((meta, i) => ({
    number: meta.number,
    icon: meta.icon,
    title: features[i].title,
    description: features[i].description,
  }));

  return (
    <section id="why" ref={sectionRef} className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-4 text-[#0193cd]">
            {sectionLabel}
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold text-foreground">{sectionTitle}</h2>
        </motion.div>

        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-border" />
          <motion.div
            className="absolute left-0 top-0 w-px origin-top"
            style={{ height: lineHeight, background: "#0193cd" }}
          />

          <div className="pl-8 md:pl-16">
            {featuresData.map((feature) => (
              <FeatureItem key={feature.number} feature={feature} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
